import { FmdGoodOutlined, ChatBubbleOutlineOutlined, CalendarMonthOutlined, LocalAtm } from '@mui/icons-material'
import { Grid, Typography,Button, Chip, FormControl, InputLabel, TextField, MenuItem, InputAdornment, Autocomplete, Divider, Box } from '@mui/material'
import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUpdateJobMutation } from '../../../Redux/api/jobApi'
import { KaziBookmark } from '../../../Shared Components/Kazi/KaziBookmark'
import { useAppDispatch, useAppSelector } from '../../../Redux/store'
import { JobApplyModal } from './JobApplyModal'
import { DurationType, Job, JobConstants, JobType, PaymentType, WorkSite } from '../../../Types/Job'
import { Client } from '../../../Types/Client'
import { Controller, useForm } from 'react-hook-form'
import { languages } from '../../../Types/General'
import { reset } from '../../../Redux/slices/JobPostingCreationSlice'
import { useToast } from '../../../Hooks/useToast'
import { Questions } from '../../../Shared Components/Job Creation/Questions/Questions'
import { ResponsibilitiesForm } from '../../../Shared Components/Job Creation/Responsibilities/ResponsibilitiesForm'
import { QualificationsForm } from '../../../Shared Components/Job Creation/Qualifications/QualificationsForm'
import { DeliverablesForm } from '../../../Shared Components/Job Creation/Deliverables/DeliverablesForm'
import { EditPostingSchema, EditPostingType } from '../../../Schemas/JobPostingSchemas'
import { yupResolver } from '@hookform/resolvers/yup';

export interface IEditPostingForm {
    title: string;
    type: string;
    work_site: string;
    primary_language: string;
    pay_amount: string;
    description: string;
    pay_rate: string;
    tags: string[];
    duration: number;
    duration_type?: string;
    responsibilities: string[];
    qualifications: string[];
    questions: string[];
    deliverables: string[];
}

interface IEditPostingProps {
    job: Job;
    client: Client;
    mode: 'view' | 'edit';
    setMode: Dispatch<SetStateAction<'view' | 'edit'>>;
}

export const EditPosting = ({job, client, mode, setMode}:IEditPostingProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const userType = useAppSelector( state => state.Global.userType)
    const [updateJob] = useUpdateJobMutation();
    const {
        control,
        handleSubmit,
        formState: {isDirty, errors, isValid},
        getValues,
        setValue
    } = useForm<EditPostingType>({
        defaultValues: {
            title: job.title ?? "",
            type: job.type ?? "",
            work_site: job.work_site ?? "",
            primary_language: job.primary_language ?? "",
            description: job.description ?? "",
            pay_amount: {pay_type: job.pay_type, min: job.pay_amount, max: job.pay_amount_max},
            pay_rate: job.pay_rate ?? "",
            tags: job.tags ?? [],
            duration: job.duration ?? 30,
            duration_type: job?.duration_type,
            responsibilities: job.responsibilities ?? [],
            qualifications: job.skills.map( s => s.skill) ?? [],
            questions: job?.questions?.map( q => q.question) ?? [],
            deliverables: job.deliverables ?? []
        },
        resolver: yupResolver(EditPostingSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
    });
    /* ******************** Variables ******************** */
    const [open, setOpen] = useState(false);
    const maxError = useMemo(() => {
        const err = errors?.pay_amount?.max;
        if (err) {
            if (err.type === 'is-greater') {
                return err.message + ` $${getValues('pay_amount.min')}`;
            } 
            return err.message;
        }
        return ''
    }, [errors.pay_amount?.max]);
    /* ******************** Functions ******************** */
    const handleCancel = () => {
        setMode('view');
        const path = window.location.pathname
        if (path.endsWith('edit')){
            const newPath = path.substring(0, path.lastIndexOf('/'))                
            navigate(newPath, { replace: true})
        }
        dispatch(reset());
    }
    const handleSave = (data: EditPostingType) => {
        // debugger
        updateJob({
            locator: job.locator,
            updated_data: {
                ...data,
                pay_type: data.pay_amount.pay_type,
                pay_amount: data.pay_amount.min,
                pay_amount_max: data.pay_amount.max,
                skills: data.qualifications.map( (q: any) => ({skill: q, qualification: 'must_have'})),
                questions: data.questions.map( (q: any, idx: number) => ({id: idx, question: q}))
            }
        })
        .unwrap()
        .then(res => {
            handleCancel();
        })
        .catch(err => {
            console.log(err);
            showToast({
                text: 'Unable to update job',
                variant: 'error'
            })
        })
    }  
    /* ******************** Effects ******************** */
    // useEffect(()=>{
    //     job.responsibilities.forEach( ( resp, idx ) => append({[`resp-${idx}`]: resp}))
    //     // dispatch(preloadJobPosting(job))
    // }, [])
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid container display={"flex"} flexDirection={"row"} sx={{color: 'primary.main'}} columnGap={15} marginBottom={6}>
                <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
                    <Grid item display={"flex"} flexDirection={"column"} gap={'16px'}>
                        <Typography variant='lora-headline-small' fontWeight={"bold"}>
                            <Controller 
                                name="title"
                                control={control}
                                rules={{required: true}}
                                render={({ field }) =>(                  
                                    <TextField
                                        {...field}                                        
                                        // error={hasErrors(field.name, errors)}
                                        variant='standard'
                                        sx={{width: '375px'}}
                                        placeholder='Job Title'
                                    />    
                                )}
                            />
                        </Typography>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <CalendarMonthOutlined />
                            <Grid display={"flex"} flexDirection={"column"} gap={1}>
                                <Grid display={"flex"} flexDirection={"row"} gap={1}>
                                    <Controller 
                                        name="type"
                                        control={control}
                                        rules={{required: true}}
                                        render={({ field }) =>(
                                            <FormControl>   
                                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Job Type"}</InputLabel>                   
                                                <TextField
                                                    {...field}
                                                    select
                                                    // error={hasErrors(field.name, errors)}
                                                    variant='standard'
                                                    sx={{
                                                        width: '110px'
                                                    }}
                                                >
                                                    {
                                                        JobType.map((type) => (
                                                            <MenuItem key={type} value={type}>
                                                                {type}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </FormControl>    
                                        )}
                                    />
                                    <Controller 
                                        name="pay_rate"
                                        control={control}
                                        rules={{required: true}}
                                        render={({ field }) =>(
                                            <FormControl>   
                                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Payment type"}</InputLabel>                   
                                                <TextField
                                                    {...field}
                                                    select
                                                    // error={hasErrors(field.name, errors)}
                                                    variant='standard'
                                                    sx={{
                                                        width: '110px'
                                                    }}
                                                >
                                                    {
                                                        PaymentType.map((rate) => (
                                                            <MenuItem key={rate} value={rate}>
                                                                {rate}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </FormControl>    
                                        )}
                                    />
                                </Grid>
                                {
                                    job.type === 'Internship' &&
                                    <Grid display={"flex"} flexDirection={"row"} gap={1}>
                                        <Controller 
                                            name="duration"
                                            control={control}
                                            rules={{required: true}}
                                            render={({ field }) =>(                 
                                                    <TextField
                                                        {...field}
                                                        // error={hasErrors(field.name, errors)}
                                                        variant='standard'
                                                        sx={{
                                                            width: '110px'
                                                        }}
                                                        type='number'
                                                    />
                                            )}
                                        />
                                        <Controller 
                                            name="duration_type"
                                            control={control}
                                            rules={{required: true}}
                                            render={({ field }) =>(
                                                <FormControl>   
                                                    <InputLabel sx={{opacity: 1, margin: -1.5}}>{field.value ?"":"Duration type"}</InputLabel>                   
                                                    <TextField
                                                        {...field}
                                                        select
                                                        // error={hasErrors(field.name, errors)}
                                                        variant='standard'
                                                        sx={{
                                                            width: '110px'
                                                        }}
                                                    >
                                                        {
                                                            DurationType.map((type) => (
                                                                <MenuItem key={type} value={type}>
                                                                    {type}
                                                                </MenuItem>
                                                            ))
                                                        }
                                                    </TextField>
                                                </FormControl>    
                                            )}
                                        />
                                    </Grid>
                                }
                            </Grid>
                        </Grid>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <LocalAtm />
                            <Grid display={"flex"} flexDirection={"column"} gap={1}>                            
                                <Grid display={"flex"} flexDirection={"row"} gap={1}>                             
                                    {/* <Controller 
                                        name="pay_amount.pay_type"
                                        control={control}
                                        rules={{required: true}}
                                        render={({ field }) =>(   
                                            <FormControl>   
                                                <InputLabel sx={{opacity: 1}}>{field.value ?"":"Pay Type"}</InputLabel>                   
                                                <TextField
                                                    {...field}
                                                    select
                                                    // error={hasErrors(field.name, errors)}
                                                    variant='standard'
                                                    sx={{
                                                        width: '110px'
                                                    }}
                                                >
                                                    {
                                                        Object.values(JobConstants.SalaryType).map((payType) => (
                                                            <MenuItem key={payType} value={payType}>
                                                                {payType}
                                                            </MenuItem>
                                                        ))
                                                    }
                                                </TextField>
                                            </FormControl>           
                                        )}
                                    />                             */}
                                    <Controller 
                                        name="pay_amount.min"
                                        control={control}
                                        rules={{required: true}}
                                        render={({ field }) =>(                  
                                            <TextField
                                                {...field}                                        
                                                // error={hasErrors(field.name, errors)}
                                                InputProps={{
                                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                }}
                                                sx={{
                                                    width: '110px'
                                                }}
                                                variant='standard'
                                                placeholder='payment'
                                            />    
                                        )}
                                    />
                                   {/* {getValues('pay_amount.pay_type') === JobConstants.SalaryType.Range && 
                                    <>
                                        <Typography variant='body-small' color={'black.200'} marginBottom={1} alignSelf={maxError!.length > 0 ? 'center': 'end'}>-</Typography>
                                        <Controller 
                                            name="pay_amount.max"
                                            control={control}
                                            rules={{required: true}}
                                            render={({ field }) =>(   
                                                <Box display={'flex'} flexDirection={'column'} flex={1}>                                                            
                                                    <TextField
                                                        {...field}                                        
                                                        error={maxError!.length > 0}
                                                        InputProps={{
                                                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                                        }}
                                                        sx={{
                                                            width: '110px'
                                                        }}
                                                        variant='standard'
                                                        placeholder='payment'
                                                    /> 
                                                    {/* @ts-ignore }
                                                    {maxError.length > 0  && <Typography variant="body-smaller" color='error' width={'100%'}>{maxError}</Typography>}
                                                </Box>          
                                            )}
                                        />
                                    </>
                                   } */}
                                </Grid>                                
                            </Grid>
                        </Grid>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <FmdGoodOutlined />
                            <Controller 
                                name="work_site"
                                control={control}
                                rules={{required: true}}
                                render={({ field }) =>(
                                    <FormControl>   
                                        <InputLabel sx={{opacity: 1}}>{field.value ?"":"Work-site"}</InputLabel>                   
                                        <TextField
                                            {...field}
                                            select
                                            // error={hasErrors(field.name, errors)}
                                            variant='standard'
                                            sx={{width: '110px'}}
                                        >
                                            {
                                                WorkSite.map((site) => (
                                                    <MenuItem key={site} value={site}>
                                                        {site}
                                                    </MenuItem>
                                                ))
                                            }
                                        </TextField>
                                    </FormControl>    
                                )}
                            />
                        </Grid>
                        <Grid item display={"flex"} flexDirection={"row"} gap={1}>
                            <ChatBubbleOutlineOutlined />
                                <Controller 
                                    name="primary_language"
                                    control={control}
                                    rules={{required: true}}
                                    render={({ field }) =>(
                                        <FormControl>   
                                            <InputLabel sx={{opacity: 1}}>{field.value ?"":"Primary langauge"}</InputLabel>                   
                                            <TextField
                                                {...field}
                                                select
                                                // error={hasErrors(field.name, errors)}
                                                variant='standard'
                                                sx={{width: '110px'}}
                                            >
                                                {
                                                    languages.map((language) => (
                                                        <MenuItem key={language} value={language}>
                                                            {language}
                                                        </MenuItem>
                                                    ))
                                                }
                                            </TextField>
                                        </FormControl>    
                                    )}
                                />
                        </Grid>
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Job description</Typography>
                            <Controller
                                name="description"
                                control={control}
                                rules={{required: true}}
                                render={({ field }) =>(            
                                    <TextField
                                        {...field}
                                        fullWidth
                                        multiline
                                        maxRows={5}
                                        // error={hasErrors(field.name, errors)}
                                        sx={{
                                            width: "100%",
                                            '& .MuiInputBase-input': {
                                            maxHeight: '126px'
                                            }
                                        }}
                                    />
                                
                                )}
                            /> 
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Responsibilities</Typography>      
                        <Grid item xs display={"flex"} flexDirection={"column"} gap={1}>
                            { <ResponsibilitiesForm control={control} /> } 
                        </Grid>
                    </Grid>
                    {/* Deliverables - Only show for Short-term jobs */}
                    {getValues('type') === "Short-term" && (
                        <Grid item display={"flex"} flexDirection={"column"} >
                            <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Deliverables</Typography>      
                            <Grid item xs display={"flex"} flexDirection={"column"} gap={1}>
                                { <DeliverablesForm control={control} /> } 
                            </Grid>
                        </Grid>
                    )}
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Qualifications</Typography>  
                        <Grid item xs display={"flex"} flexDirection={"column"} gap={1} >
                            { <QualificationsForm control={control} />}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item flex={1} display={"flex"} flexDirection={"column"} gap={4}>
                    <Grid item display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} marginBottom={16}>
                        <Grid display={'flex'} flexDirection={'row'}>
                            <Grid>
                                {userType === 'talent' && <KaziBookmark locator={job?.locator ?? ''} sx={{padding: '4px 8px'}}/>}
                            </Grid>
                            <Grid>
                                <Button variant={'contained'} size='large' onClick={handleCancel} sx={{marginX: 2}} >Cancel</Button>
                                <Button variant={'contained'} size='large' onClick={handleSubmit(handleSave)} disabled={!isValid}>Save</Button>
                            </Grid>
                        </Grid > 
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} >
                        <Typography variant='lora-title-medium' fontWeight={"bold"} marginBottom={'16px'}>Skills</Typography>
                        <Grid item display={"flex"} flexDirection={"row"} flexWrap={'wrap'} gap={1} width={'55%'}>
                            <Controller
                                name='tags'
                                control={control}
                                render={({field}) =>(
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[]}
                                        defaultValue={getValues('tags')}
                                        onChange={(params, val) => setValue('tags', val, {shouldDirty: true})}
                                        renderTags={(value, props) =>{
                                            return value.map((option, index) => (
                                            //@ts-ignore
                                            <Chip  variant='outlined' label={option} {...props({ index })} />
                                            ))
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={field.value.length == 0?"Type in a skill and press enter": "CSS, Excel, etc."}
                                            />
                                            )}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid item display={"flex"} flexDirection={"column"} gap={'16px'}>
                        <Typography variant='lora-title-medium' fontWeight={"bold"} >Questions</Typography>
                        <Grid item xs display={"flex"} flexDirection={"column"} gap={1}>
                                { <Questions control={control} />} 
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <JobApplyModal 
                open={open} 
                setOpen={setOpen} 
                posting={job}
                clientInfo={{
                    locator: client?.locator!, 
                    first_name: client?.first_name!, 
                    last_name: client?.last_name!, 
                    profile_picture: client?.profile_picture!,
                    company: client?.company!
                }} 
            />
        </>
    )
}